import { useCallback, useMemo, useState, type ReactElement } from 'react'
import { useQueryState } from 'nuqs'
import { parseAsUserId } from '@util'
import {
  compareRoles,
  getName,
  isDefined,
  isEmpty,
  isNotEmpty,
  searchIncludes,
  teamMemberIsManager,
  type TeamId,
  type TeamMemberData,
  type TinyUser,
  type UserId,
} from '@terros/common'
import { useNavigate } from '@tanstack/react-router'
import { showNotification } from '@mantine/notifications'
import { useDisclosure } from '@mantine/hooks'
import { Alert, Group, Stack, Text, TextInput } from '@mantine/core'
import { useToastError } from '@hooks'
import { sortUsersByLastName, useAddTeamMember, usePermissionCheck, usePreference, useRoles } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'
import { TDrawer, TListItem, TSectionList } from '../terros'
import { PickUserDrawer } from '../pickers'
import { PermissionTooltip } from '../permissions'
import { PrefetchedUserInfo } from '../entityInfo'
import { ButtonAdd } from '../buttons'
import { ViewTeamMembership } from './viewMembership'

type Props = {
  teamId: TeamId
  members: TeamMemberData[]
  isArchived: boolean
}

export const TeamMembers = ({ members, teamId, isArchived }: Props): ReactElement => {
  const [isOpen, { open, close }] = useDisclosure()
  const { add, isAdding, error: addError } = useAddTeamMember()
  useToastError(addError)
  const { data: roles } = useRoles()
  const navigate = useNavigate()
  const { data: canUpdateTeamMembership } = usePermissionCheck({ permissionType: 'team:update', targetId: teamId })
  const { data: managerBasedTeams } = usePreference('managerBasedTeams', teamId)

  const [userMembership, setUserMembership] = useQueryState('userMembership', parseAsUserId)
  const [search, setSearch] = useState('')

  const filteredMembers = useMemo(() => {
    return members.filter((member) => {
      const searchArray = [
        member.user.firstName,
        member.user.lastName,
        member.user.preferredName,
        member.user.email,
        member.user.userId,
        getName(member.user),
      ]
      return searchIncludes(searchArray, search)
    })
  }, [members, search])

  const onAddMember = useCallback(
    async (user: TinyUser): Promise<void> => {
      if (isArchived) return
      const userId = user.userId

      close()

      await add({ teamId, userId })
      showNotification({
        message: 'Member added successfully',
        autoClose: 5000,
      })
    },
    [close, add, teamId, isArchived]
  )

  const sortedMembers = useMemo(() => {
    return filteredMembers.sort((a, b) => {
      const aIsManager = teamMemberIsManager(a, roles)
      const bIsManager = teamMemberIsManager(b, roles)
      if (aIsManager && !bIsManager) return -1
      if (!aIsManager && bIsManager) return 1
      return sortUsersByLastName(a.user, b.user)
    })
  }, [filteredMembers, roles])

  const selectedUser = useMemo(() => members.find((m) => m.user.userId === userMembership), [members, userMembership])
  const handleUserClick = useCallback(
    (userId: UserId): void => {
      if (!canUpdateTeamMembership) {
        navigate({ to: `/team/${teamId}/${userId}` })
      } else {
        setUserMembership(userId)
      }
    },
    [canUpdateTeamMembership, navigate, setUserMembership, teamId]
  )
  const handleCloseUserMembership = useCallback(() => {
    setUserMembership(null)
  }, [setUserMembership])

  return (
    <>
      <PickUserDrawer
        canAddUser
        close={close}
        drawerProps={{ id: 'add-team-member' }}
        isOpen={isOpen && !isArchived}
        options={{ downline: true }}
        headerButtonProps={{
          teamId,
        }}
        onSelect={onAddMember}
      />
      <TSectionList
        heading={{
          title: 'Members',
          children: !isArchived && (
            <PermissionTooltip
              permissionType='team:update'
              extraConditions={[
                {
                  active: managerBasedTeams ?? false,
                  label: 'Team members are managed automatically and cannot be added manually.',
                },
              ]}
              targetId={teamId}
            >
              <ButtonAdd loading={isAdding} variant='ghost' onClick={open} />
            </PermissionTooltip>
          ),
        }}
      >
        <TextInput
          placeholder='Search members'
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          mb='xs'
        />
        {sortedMembers.map((member) => (
          <TListItem key={member.user.userId} onClick={() => handleUserClick(member.user.userId)}>
            <MemberRow member={member} />
          </TListItem>
        ))}
        {isEmpty(members) && <Alert icon={<MaterialSymbolIcon icon='info' />}>No members</Alert>}
      </TSectionList>
      <TDrawer
        opened={isDefined(selectedUser)}
        title={selectedUser ? getName(selectedUser.user) : 'User Membership'}
        onClose={handleCloseUserMembership}
      >
        {selectedUser && <ViewTeamMembership teamId={teamId} userId={selectedUser.user.userId} />}
      </TDrawer>
    </>
  )
}

const MemberRow = ({ member }: { member: TeamMemberData }): ReactElement => {
  const { user } = member
  const rolesString = useUserRoles(member)

  return (
    <Group>
      <PrefetchedUserInfo avatarOnly linked={false} size='md' user={user} />
      <Stack align='start' gap={0}>
        <Text fw={700}>{getName(user)}</Text>
        {isNotEmpty(rolesString) && (
          <Text c='dimmed' size='sm'>
            {rolesString}
          </Text>
        )}
      </Stack>
    </Group>
  )
}

const useUserRoles = (membership: TeamMemberData): string | undefined => {
  const { roleIds: userRoleIds = [] } = membership
  const { data = [], isLoading } = useRoles({ companyId: membership.user.companyId })
  if (isLoading) return

  const roles = userRoleIds
    .map((r) => data.find((role) => role.roleId === r))
    .filter(isDefined)
    .sort(compareRoles)

  return roles.map((r) => r.name).join(', ')
}
